import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Card, Tag, Badge, Button, Select, message, Drawer, Descriptions, Divider, Typography, Space, Spin, Alert } from 'antd';
import { EditDocsSales } from "src/features/Modal";
import {
  ClockCircleOutlined,
  UserOutlined,
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ShoppingCartOutlined,
  EnvironmentOutlined,
  TagOutlined,
  IdcardOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import "./DocsSalesCalendar.css";

const defaultStatusGroups = {
  received: { title: 'Новый заказ', color: 'blue' },
  processed: { title: 'В обработке', color: 'geekblue' },
  collecting: { title: 'Сборка начата', color: 'orange' },
  collected: { title: 'Собран', color: 'purple' },
  picked: { title: 'Передан курьеру', color: 'cyan' },
  delivered: { title: 'Доставлен', color: 'green' }
};

const fullStatusGroups = {
  ...defaultStatusGroups,
  success: { title: 'Успешно завершен', color: 'green' },
  closed: { title: 'Закрыт', color: 'magenta' }
};

const defaultStatusOrder = ['received','processed','collecting','collected','picked','delivered'];

const { Title } = Typography;

const isUnix10 = (n) => typeof n === 'number' && String(n).length === 10;

const formatDate = (val) => {
  if (val === null || val === undefined) return 'Не указано';
  if (isUnix10(val)) return moment.unix(val).format('DD.MM.YYYY HH:mm');
  if (moment.isMoment(val)) return val.format('DD.MM.YYYY HH:mm');
  return String(val);
};

const prettyLabel = (key) => {
  const map = {
    id: 'ID',
    number: 'Номер',
    dated: 'Дата документа',
    operation: 'Операция',
    tags: 'Теги',
    docs_sales: 'Документ продажи',
    nomenclature_count: 'Позиции (шт.)',
    paid_doc: 'Оплачено (док.)',
    paid_rubles: 'Оплачено (₽)',
    paid_loyality: 'Оплачено (баллы)',
    status: 'Активен',
    doc_discount: 'Скидка, %',
    comment: 'Комментарий',
    client: 'Клиент ID',
    contragent: 'Контрагент ID',
    contragent_name: 'Контрагент',
    contract: 'Договор ID',
    organization: 'Организация ID',
    warehouse: 'Склад ID',
    autorepeat: 'Автоповтор',
    settings: 'Настройки',
    sum: 'Сумма, ₽',
    tax_included: 'Цены с НДС',
    tax_active: 'НДС включён',
    sales_manager: 'Менеджер ID',
    goods: 'Товары',
    delivery_info: 'Доставка',
    updated_at: 'Обновлено',
    created_at: 'Создано',
    has_contragent: 'Есть контрагент',
    has_loyality_card: 'Есть карта лояльности',
    color_status: 'Цвет статуса',
    priority: 'Приоритет',
    order_status: 'Статус заказа',
    assigned_picker: 'Сборщик',
    assigned_courier: 'Курьер',
    picker_started_at: 'Начало сборки',
    picker_finished_at: 'Окончание сборки',
    courier_picked_at: 'Передан курьеру',
    courier_delivered_at: 'Доставлен',
    contragent_segments: 'Сегменты контрагента (ID)'
  };
  return map[key] || key;
};

const humanize = (key, val) => {
  const formatPerson = (a) => {
    if (a === null || a === undefined) return 'Не назначен';

    // Если массив — попробуем первый элемент
    if (Array.isArray(a) && a.length > 0) return formatPerson(a[0]);
    if (Array.isArray(a)) return 'Не назначен';

    // Если вложенный объект под ключом id (иногда приходит { id: { ... } })
    if (typeof a === 'object' && a.id && typeof a.id === 'object') {
      return formatPerson(a.id);
    }

    // Если строка или число — это, вероятно, просто ID
    if (typeof a === 'string' || typeof a === 'number') {
      return String(a);
    }

    // обычный объект — пробуем получить first_name/last_name и варианты
    const first = a.first_name ?? a.first ?? a.name ?? a.given_name ?? '';
    const second = a.last_name ?? a.last_name ?? a.surname ?? a.family_name ?? '';
    const full = [first, second].filter(Boolean).join(' ');
    if (full) return full;

    // альтернативные варианты
    if (a.full_name) return a.full_name;
    if (a.display_name) return a.display_name;
    if (a.label) return a.label;

    // если остался только id в объекте — покажем "ID: <id>"
    if (a.id && (typeof a.id === 'string' || typeof a.id === 'number')) {
      return `ID: ${a.id}`;
    }

    // телефон/контакты можно показать как fallback
    if (a.phone) return a.phone;
    if (a.email) return a.email;

    // в крайнем случае — JSON (коротко)
    try {
      const s = JSON.stringify(a);
      return s.length > 120 ? s.slice(0, 120) + '…' : s;
    } catch (e) {
      return String(a);
    }
  };

  // Специфичная обработка для полей сборщика/курьера
  if (['assigned_picker', 'assigned_courier'].includes(key)) {
    return formatPerson(val);
  }

  // Общие правила для остальных полей
  if (val === null || val === undefined) return 'Не указано';
  if (typeof val === 'boolean') return val ? 'Да' : 'Нет';
  if (moment.isMoment && moment.isMoment(val)) return formatDate(val);
  if (['dated', 'updated_at', 'created_at'].includes(key)) return formatDate(val);
  if (key === 'nomenclature_count') return `${Number(val || 0)} шт.`;

  if (Array.isArray(val)) {
    if (val.length === 0) return 'Не указано';
    if (val.every(v => typeof v === 'number')) return val.join(', ');
    if (val.every(v => typeof v === 'string')) return val.join(', ');
    try { return JSON.stringify(val, null, 2); } catch (e) { return String(val); }
  }

  if (key === 'sum' || key === 'paid_rubles') return `${Number(val || 0).toLocaleString('ru-RU')} ₽`;
  if (key === 'doc_discount') return `${val}%`;
  if (typeof val === 'object') {
    try { return JSON.stringify(val, null, 2); } catch (e) { return String(val); }
  }
  if (isUnix10(val)) return formatDate(val);
  return String(val);
};

const renderFullPaid = (row) => {
  const paid_doc = Number(row?.paid_doc || 0);
  const sum = Number(row?.sum || 0);
  const doc_discount = Number(row?.doc_discount || 0);
  if (paid_doc >= sum - doc_discount) {
    return (<span style={{ color: "green" }}><CheckCircleOutlined /> Оплачено</span>);
  }
  return (<span style={{ color: "red" }}><CloseCircleOutlined /> Не оплачено</span>);
};

const getOrderDateMoment = (order, mode = 'delivery') => {
  if (!order) return null;
  if (mode === 'delivery') {
    const d = order.delivery_info?.delivery_date ?? order.delivery_date ?? null;
    if (!d) return null;
    if (isUnix10(d)) return moment.unix(d);
    if (moment.isMoment && moment.isMoment(d)) return d.clone();
    const parsed = moment(d);
    return parsed.isValid() ? parsed : null;
  }
  const c = order.created_at ?? order.created ?? order.dated ?? null;
  if (!c) return null;
  if (isUnix10(c)) return moment.unix(c);
  if (moment.isMoment && moment.isMoment(c)) return c.clone();
  const parsedC = moment(c);
  return parsedC.isValid() ? parsedC : null;
};

const SimpleCalendar = ({ value, onSelect }) => {
  const [viewMonth, setViewMonth] = useState(value.clone().startOf('month'));
  useEffect(() => { setViewMonth(value.clone().startOf('month')); }, [value]);

  const start = viewMonth.clone().startOf('month').startOf('week');
  const weeks = [];
  let day = start.clone();
  for (let i = 0; i < 6; i++) {
    const days = [];
    for (let j = 0; j < 7; j++) {
      days.push(day.clone());
      day.add(1, 'day');
    }
    weeks.push(days);
  }

  const isSameDay = (a, b) => a && b && a.isSame(b, 'day');

  return (
    <div className="simple-calendar">
      <div className="sc-header">
        <Button size="small" onClick={() => setViewMonth(m => m.clone().subtract(1, 'month'))}>&lt;</Button>
        <div className="sc-month-label">{viewMonth.format('MMMM YYYY')}</div>
        <Button size="small" onClick={() => setViewMonth(m => m.clone().add(1, 'month'))}>&gt;</Button>
      </div>

      <div className="sc-grid">
        <div className="sc-weekdays">
          {moment.weekdaysShort().map(d => (<div key={d} className="sc-weekday">{d}</div>))}
        </div>

        <div className="sc-weeks">
          {weeks.map((week, i) => (
            <div key={i} className="sc-week">
              {week.map(d => (
                <button
                  key={d.format('YYYY-MM-DD')}
                  className={`sc-day ${d.month() !== viewMonth.month() ? 'sc-day--muted' : ''} ${isSameDay(d, moment()) ? 'sc-day--today' : ''} ${isSameDay(d, value) ? 'sc-day--selected' : ''}`}
                  onClick={() => onSelect(d)}
                >
                  {d.date()}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const DocsSalesCalendar = ({ dataSource, token, updateRow, tags, visibleStatuses = [] }) => {

  const dataSourceRef = useRef(dataSource);
  useEffect(() => {
    dataSourceRef.current = dataSource;
  }, [dataSource]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [dateMode, setDateMode] = useState('delivery');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  
  // Новые состояния для загрузки данных
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localData, setLocalData] = useState([]);
  const [currentDate, setCurrentDate] = useState(moment().startOf('day')); // Перемещено выше
  const [loadingStatus, setLoadingStatus] = useState({});
  const [datePickerVisible, setDatePickerVisible] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('kanban') !== 'true') {
        params.set('kanban', 'true');
        window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Функция для загрузки данных в зависимости от режима
  const fetchOrders = useCallback(async (date, mode) => {
    setLoading(true);
    setError(null);
    try {
      const dateStr = date.format('YYYY-MM-DD');
      let url;
      
      if (mode === 'created') {
        url = `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/created/${dateStr}/`;
      } else {
        url = `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/delivery/${dateStr}/`;
      }
      
      const response = await axios.get(url, { 
        params: { token, limit: 1000 } 
      });
      
      setLocalData(response.data.result || []);
    } catch (error) {
      setError(error);
      message.error('Не удалось загрузить заказы');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Эффект для загрузки данных при изменении даты или режима
  useEffect(() => {
    fetchOrders(currentDate, dateMode);
  }, [currentDate, dateMode, fetchOrders]);

  // Эффект для обработки dataSource из props
  useEffect(() => {
    if (dataSource) {
      const normalizedData = normalizeDataSource(dataSource);
      setLocalData(normalizedData);
    }
  }, [dataSource]);

  const normalizeDataSource = (ds) => {
    if (!ds) return [];
    if (Array.isArray(ds)) return ds;
    if (ds.result && Array.isArray(ds.result)) return ds.result;
    return [];
  };

  const fetchOrderDetails = async (orderId) => {
    setLoadingDetails(true);
    setErrorDetails(null);
    try {
      const response = await axios.get(`https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/${orderId}/`, { params: { token } });
      return response.data;
    } catch (error) {
      setErrorDetails(error);
      message.error('Не удалось загрузить детали заказа');
      return null;
    } finally {
      setLoadingDetails(false);
    }
  };

  const openDetails = async (order) => {
    const originalOrder = { ...order };
    setSelectedOrder(originalOrder);
    setDetailsOpen(true);
    const fullOrder = await fetchOrderDetails(order.id);
    if (fullOrder) {
      const mergedOrder = {
        ...originalOrder,
        ...fullOrder,
        nomenclature_count: fullOrder.nomenclature_count ?? originalOrder.nomenclature_count,
        contragent_name: fullOrder.contragent_name ?? originalOrder.contragent_name,
        sum: fullOrder.sum ?? originalOrder.sum,
        dated: fullOrder.dated ?? originalOrder.dated
      };
      setSelectedOrder(mergedOrder);
      setLocalData(prev => prev.map(o => o.id === order.id ? mergedOrder : o));
    }
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedOrder(null);
    setErrorDetails(null);
  };

  const visibleStatusKeys = useMemo(() => {
    const extras = Array.isArray(visibleStatuses) ? visibleStatuses : [];
    const allowedExtras = extras.filter(s => ['success', 'closed'].includes(s));
    const merged = [...defaultStatusOrder];
    allowedExtras.forEach(s => { if (!merged.includes(s)) merged.push(s); });
    return merged;
  }, [visibleStatuses]);

  const groupedOrders = useMemo(() => {
    const groups = {};
    localData.forEach(order => {
      const dateMoment = getOrderDateMoment(order, dateMode);
      if (!dateMoment) return;
      const date = dateMoment.format('YYYY-MM-DD');
      if (!groups[date]) groups[date] = {};

      const statusCandidate = order.order_status;
      let statusGroup;
      if (typeof statusCandidate === 'string' && statusCandidate) statusGroup = statusCandidate;
      else statusGroup = 'received';

      if (!groups[date][statusGroup]) groups[date][statusGroup] = [];
      groups[date][statusGroup].push(order);
    });
    return groups;
  }, [localData, dateMode]);

  const dayOrders = useMemo(() => {
    const dateKey = currentDate.format('YYYY-MM-DD');
    return groupedOrders[dateKey] || {};
  }, [groupedOrders, currentDate]);

  const calculateStatusSums = useCallback((orders) => {
    const sums = {};
    visibleStatusKeys.forEach(status => {
      sums[status] = (orders[status] || []).reduce((acc, order) => acc + (Number(order.sum) || 0), 0);
    });
    return sums;
  }, [visibleStatusKeys]);

  const statusSums = useMemo(
    () => calculateStatusSums(dayOrders),
    [dayOrders, calculateStatusSums]
  );

  const updateOrderStatusLocal = (orderId, newStatus) => {
    setLocalData(prev => prev.map(o => o.id === orderId ? { ...o, order_status: newStatus } : o));
  };

  const handleStatusChange = async (orderId, newStatus) => {
    const order = localData.find(o => o.id === orderId);
    if (!order) { message.error('Заказ не найден в локальных данных'); return; }
    const organization = order.organization;
    if (organization === undefined || organization === null) { message.error('Не указана organization у заказа — нельзя отправить патч'); return; }

    const prevStatus = (typeof order.order_status === 'string' && order.order_status) || 'received';

    // оптимистично-показать статус в UI
    updateOrderStatusLocal(orderId, newStatus);
    setLoadingStatus(prev => ({ ...prev, [orderId]: true }));

    try {
      // основной patch (тот, который вы уже используете)
      await axios.patch(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/${orderId}/`,
        [{ id: orderId, organization: organization, order_status: newStatus }],
        { params: { token } }
      );

      // После успешного патча — явно запросим полный объект заказа,
      // который в бекенде уже будет содержать assigned_picker/assigned_courier как объекты
      const fetchResp = await axios.get(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/${orderId}/`,
        { params: { token } }
      );

      const fullOrder = fetchResp && fetchResp.data ? fetchResp.data : null;
      if (fullOrder) {
        // обновляем локально весь объект (merge)
        setLocalData(prev => prev.map(o => o.id === orderId ? { ...o, ...fullOrder } : o));
        // обновляем Drawer если открыт для этого заказа
        if (selectedOrder && selectedOrder.id === orderId) {
          setSelectedOrder(prev => ({ ...(prev || {}), ...fullOrder }));
        }
      }

      message.success('Статус обновлен');
    } catch (error) {
      console.error('Ошибка при обновлении статуса:', error);
      message.error('Ошибка при обновлении статуса на сервере — откатываем локально');
      // откатываем локально
      updateOrderStatusLocal(orderId, prevStatus);
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder(prev => prev ? { ...prev, order_status: prevStatus } : prev);
      }
    } finally {
      setLoadingStatus(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleDropToStatus = async (targetStatus, e) => {
    e.preventDefault();
    const idStr = e.dataTransfer.getData('text/plain');
    if (!idStr) return;
    const orderId = isNaN(Number(idStr)) ? idStr : Number(idStr);
    const order = localData.find(o => o.id === orderId);
    if (!order) return;
    const currentStatus = order.order_status || 'received';

    // оптимистично обновляем статус
    updateOrderStatusLocal(orderId, targetStatus);
    setLoadingStatus(prev => ({ ...prev, [orderId]: true }));

    try {
      // вызываем endpoint /status
      await axios.patch(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/${orderId}/status`,
        { status: targetStatus },
        { params: { token } }
      );

      // после успешного перехода получаем полный заказ с сервера
      const fetchResp = await axios.get(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/${orderId}/`,
        { params: { token } }
      );
      const fullOrder = fetchResp && fetchResp.data ? fetchResp.data : null;
      if (fullOrder) {
        setLocalData(prev => prev.map(o => o.id === orderId ? { ...o, ...fullOrder } : o));
        if (selectedOrder && selectedOrder.id === orderId) {
          setSelectedOrder(prev => ({ ...(prev || {}), ...fullOrder }));
        }
      }

      message.success(`Статус обновлен: ${fullStatusGroups[targetStatus]?.title ?? targetStatus}`);
    } catch (error) {
      console.error('Ошибка при обновлении статуса:', error);
      message.error('Ошибка при обновлении статуса - откат изменений');
      updateOrderStatusLocal(orderId, currentStatus);
    } finally {
      setLoadingStatus(prev => ({ ...prev, [orderId]: false }));
    }
  };

   return (
     <div className="order-calendar">
      <Spin spinning={loading} tip="Загрузка заказов...">
        {error && (
          <Alert
            message="Ошибка загрузки"
            description={error.message}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
      <div className="calendar-header">
        {isMobile ? (
          <>
            <div className="mobile-header-top">
              <Button icon={<LeftOutlined />} onClick={() => setCurrentDate(prev => prev.clone().subtract(1, 'days'))} />
              <Button type="primary" onClick={() => setCurrentDate(moment().startOf('day'))}>Сегодня</Button>
              <Button icon={<RightOutlined />} onClick={() => setCurrentDate(prev => prev.clone().add(1, 'days'))} />
            </div>
            <div className="mobile-header-bottom">
              <div className="current-date" onClick={() => setDatePickerVisible(!datePickerVisible)}>
                {currentDate.format('DD MMMM YYYY')}
                <CalendarOutlined />
              </div>
              {datePickerVisible && (
                <div className="date-picker-popover mobile">
                  <SimpleCalendar
                    value={currentDate}
                    onSelect={(date) => {
                      if (date) setCurrentDate(date.clone().startOf('day'));
                      setDatePickerVisible(false);
                    }}
                  />
                </div>
              )}
              <Select value={dateMode} onChange={setDateMode} size="small">
                <Select.Option value="delivery">Доставка</Select.Option>
                <Select.Option value="created">Создание</Select.Option>
              </Select>
            </div>
          </>
        ) : (
          <>
            <Button icon={<LeftOutlined />} onClick={() => setCurrentDate(prev => prev.clone().subtract(1, 'days'))} />
            <div className="date-control">
              <Button type="primary" onClick={() => setCurrentDate(moment().startOf('day'))}>Сегодня</Button>
              <div className="current-date" onClick={() => setDatePickerVisible(!datePickerVisible)}>
                {currentDate.format('DD MMMM YYYY')}
                <CalendarOutlined />
              </div>
              {datePickerVisible && (
                <div className="date-picker-popover">
                  <SimpleCalendar value={currentDate} onSelect={(date) => { if (date) setCurrentDate(date.clone().startOf('day')); setDatePickerVisible(false); }} />
                </div>
              )}
            </div>
            <Select value={dateMode} onChange={setDateMode} size="small">
              <Select.Option value="delivery">По дате доставки</Select.Option>
              <Select.Option value="created">По дате создания</Select.Option>
            </Select>
            <Button icon={<RightOutlined />} onClick={() => setCurrentDate(prev => prev.clone().add(1, 'days'))} />
          </>
        )}
      </div>

      <div className={`day-columns ${visibleStatusKeys.length > 6 ? 'allow-scroll' : ''} ${isMobile ? 'mobile-view' : ''}`}>
        {visibleStatusKeys.map((key) => {
          const group = fullStatusGroups[key];
          if (!group) return null;
          const orders = dayOrders[key] || [];
          const statusSum = statusSums[key] || 0;

          return (
            <div
              key={key}
              className="status-column"
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
              onDragLeave={(e) => { e.currentTarget.classList.remove('drag-over'); }}
              onDrop={(e) => { e.currentTarget.classList.remove('drag-over'); handleDropToStatus(key, e); }}
            >
              <div className="column-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tag color={group.color}>{group.title}</Tag> {/* Всегда показываем полное название */}
                  <Badge count={orders.length} style={{ backgroundColor: group.color }} />
                </div>

                <div style={{ marginTop: 4, fontWeight: 500, fontSize: 14, color: statusSum > 0 ? '#1d39c4' : '#8c8c8c' }}>
                  {statusSum >= 0 ? `${statusSum.toLocaleString('ru-RU')} ₽` : 'Нет заказов'}
                </div>
              </div>

              <div className="orders-list">
                {orders.map(order => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    dateMode={dateMode}
                    onStatusChange={handleStatusChange}
                    loading={loadingStatus[order.id]}
                    onOpenDetails={() => openDetails(order)}
                    isMobile={isMobile}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Drawer
        title={selectedOrder ? `Заказ #${selectedOrder.number || selectedOrder.id}` : 'Детали'}
        open={detailsOpen}
        onClose={closeDetails}
        width={window && window.innerWidth < 768 ? '100%' : 720}
        destroyOnClose
        extra={selectedOrder && (
          <Space>
            <EditDocsSales
              doc={selectedOrder}
              token={token}
              tags={tags}
              updateRow={(data, response) => {
                if (typeof updateRow === 'function') {
                  try {
                    updateRow(data, response);
                  } catch (e) {
                    console.error('updateRow prop error', e);
                  }
                  try {
                    const params = new URLSearchParams(window.location.search);
                    params.set('kanban', 'true');
                    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
                  } catch (e) { /* ignore */ }
                }
                const updated = (response && response.data) ? response.data : data;
                if (updated && updated.id) {
                  setSelectedOrder(prev => ({ ...(prev || {}), ...updated }));
                  setLocalData(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o));
                }
              }}
            />
            <Button onClick={() => { navigator.clipboard?.writeText(JSON.stringify(selectedOrder, null, 2)); message.success('JSON скопирован'); }}>
              Скопировать JSON
            </Button>
          </Space>
        )}
      >
        {loadingDetails ? (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Title level={4}>Загрузка деталей заказа...</Title>
            <p>Пожалуйста, подождите</p>
          </div>
        ) : errorDetails ? (
          <div style={{ textAlign: 'center', padding: '24px', color: '#ff4d4f' }}>
            <Title level={4} style={{ color: '#ff4d4f' }}>Ошибка загрузки</Title>
            <p>{errorDetails.message || 'Неизвестная ошибка'}</p>
            <Button type="primary" onClick={() => openDetails(selectedOrder)} style={{ marginTop: 16 }}>Повторить попытку</Button>
          </div>
        ) : selectedOrder ? (
          <div className="order-details-drawer">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ fontSize: 12, color: '#666' }}>Статус заказа:</div>
                <Select
                  value={selectedOrder.order_status || 'received'}
                  onChange={(v) => {
                    setSelectedOrder(prev => ({ ...(prev || {}), order_status: v }));
                    handleStatusChange(selectedOrder.id, v);
                  }}
                  size="small"
                  style={{ minWidth: 240 }}
                  loading={Boolean(loadingStatus[selectedOrder.id])}
                >
                  {visibleStatusKeys.map(k => (
                    <Select.Option key={k} value={k}>
                      {fullStatusGroups[k]?.title ?? k}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <div>
                <Tag color={fullStatusGroups[selectedOrder.order_status]?.color || 'default'}>
                  {fullStatusGroups[selectedOrder.order_status]?.title || selectedOrder.order_status}
                </Tag>
              </div>
            </div>

            <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>Общая информация</Title>
            <Descriptions column={1} size="small" bordered>
              {[
                'id','number','operation','contragent_name','sum','priority','color_status','order_status','nomenclature_count'
              ].map((k) => (
                <Descriptions.Item key={k} label={prettyLabel(k)}>
                  {humanize(k, selectedOrder[k])}
                </Descriptions.Item>
              ))}
            </Descriptions>

            <Divider />

            <Title level={5}>Платежи и налоги</Title>
            <Descriptions column={1} size="small" bordered>
              {[
                'paid_doc','paid_rubles','paid_loyality','doc_discount','tax_included','tax_active','has_loyality_card'
              ].map((k) => (
                <Descriptions.Item key={k} label={prettyLabel(k)}>
                  {humanize(k, selectedOrder[k])}
                </Descriptions.Item>
              ))}
              <Descriptions.Item key="full_paid" label="Оплачен полностью">
                {renderFullPaid(selectedOrder)}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Title level={5}>Связи / объекты</Title>
            <Descriptions column={1} size="small" bordered>
              {[
                'client','contragent','contragent_segments','contract','organization','warehouse','sales_manager'
              ].map((k) => (
                <Descriptions.Item key={k} label={prettyLabel(k)}>
                  {Array.isArray(selectedOrder[k]) ? selectedOrder[k].join(', ') : humanize(k, selectedOrder[k])}
                </Descriptions.Item>
              ))}
            </Descriptions>

            <Divider />

            <Title level={5}>Время</Title>
            <Descriptions column={1} size="small" bordered>
              {['dated','created_at','updated_at'].map((k) => (
                <Descriptions.Item key={k} label={prettyLabel(k)}>
                  {humanize(k, selectedOrder[k])}
                </Descriptions.Item>
              ))}
            </Descriptions>

            <Divider />

            <Title level={5}>Сборка и доставка</Title>
            <Descriptions column={1} size="small" bordered>
              {[
                'assigned_picker','assigned_courier','picker_started_at','picker_finished_at','courier_picked_at','courier_delivered_at'
              ].map((k) => (
                <Descriptions.Item key={k} label={prettyLabel(k)}>
                  {humanize(k, selectedOrder[k])}
                </Descriptions.Item>
              ))}
            </Descriptions>

            <Divider />

            <Title level={5}>Доставка</Title>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Адрес">{selectedOrder.delivery_info?.address || 'Не указано'}</Descriptions.Item>
              <Descriptions.Item label="Дата доставки">
                {selectedOrder.delivery_info?.delivery_date ? formatDate(selectedOrder.delivery_info.delivery_date) : 'Не указано'}
              </Descriptions.Item>
              <Descriptions.Item label="Получатель">
                {selectedOrder.delivery_info?.recipient ? (() => {
                  const r = selectedOrder.delivery_info.recipient;
                  const name = [r?.surname, r?.name].filter(Boolean).join(' ');
                  const phone = r?.phone ? `, ${r.phone}` : '';
                  return name || phone ? `${name}${phone}` : 'Не указано';
                })() : 'Не указано'}
              </Descriptions.Item>
              <Descriptions.Item label="Примечание">{selectedOrder.delivery_info?.note || 'Не указано'}</Descriptions.Item>
            </Descriptions>

            <Divider />

            <Title level={5}>Прочее</Title>
            <Descriptions column={1} size="small" bordered>
              {['tags','comment','docs_sales','goods','delivery_info','settings','nomenclature_count','status'].map((k) => (
                <Descriptions.Item key={k} label={prettyLabel(k)}>
                  {humanize(k, selectedOrder[k])}
                </Descriptions.Item>
              ))}
            </Descriptions>

            <Divider />

            <Title level={5}>JSON</Title>
            <pre className="order-json-block">{JSON.stringify(selectedOrder, null, 2)}</pre>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Title level={4}>Информация о заказе не найдена</Title>
            <p>Попробуйте выбрать другой заказ</p>
          </div>
        )}
      </Drawer>
      </Spin>
    </div>
  );
};

const OrderCard = ({ order, dateMode = 'delivery', onStatusChange, loading, onOpenDetails, isMobile }) => {
  const [isDragging, setIsDragging] = useState(false);
  const priorityMeta = (p => {
    if (p === null || p === undefined || p === '') return null;
    const n = Number(p);
    if (Number.isNaN(n)) return { label: String(p), color: 'default', value: String(p) };
    const val = Math.max(0, Math.min(10, Math.floor(n)));
    let color = 'default';
    if (val >= 8) color = 'red';
    else if (val >= 5) color = 'orange';
    return { label: String(val), color, value: val };
  })(order.priority);

  const onDragStart = (e) => { 
    e.dataTransfer.setData('text/plain', String(order.id)); 
    e.dataTransfer.effectAllowed = 'move'; 
    setIsDragging(true); 
  };
  
  const onDragEnd = () => setTimeout(() => setIsDragging(false), 50);
  const handleClick = (e) => { if (isDragging) return; onOpenDetails(order); };
  const handleKeyDown = (e) => { 
    if (e.key === 'Enter' || e.key === ' ') { 
      e.preventDefault(); 
      if (!isDragging) onOpenDetails(order); 
    } 
  };

  const createdMoment = getOrderDateMoment(order, 'created');
  const deliveryMoment = getOrderDateMoment(order, 'delivery');
  const contragentLabel = order.contragent_name || 'Не указан';

  const renderAssignee = (a) => {
    if (a === null || a === undefined) return 'Не назначен';

    if (Array.isArray(a)) {
      if (a.length === 0) return 'Не назначен';
      return renderAssignee(a[0]);
    }

    if (typeof a === 'object' && a.id && typeof a.id === 'object') {
      return renderAssignee(a.id);
    }

    if (typeof a === 'string' || typeof a === 'number') return String(a);

    const first = (a.first_name ?? a.first ?? a.name ?? a.given_name ?? '').toString().trim();
    const second = (a.last_name ?? a.second ?? a.last_name ?? a.surname ?? '').toString().trim();
    const full = [first, second].filter(Boolean).join(' ');

    const display =
      full ||
      a.full_name ||
      a.display_name ||
      (a.label ? a.label : null) ||
      (a.id && (typeof a.id === 'string' || typeof a.id === 'number') ? `ID: ${a.id}` : 'Не назначен');

    return <span className="assignee-name" title={display}>{display}</span>;
  };

  const statusInfo = fullStatusGroups[order.order_status] || { title: order.order_status || '—', color: 'default' };
  const deliveryAddress = order?.delivery_info?.address || '';

  return (
    <Card
      className={`order-card ${isDragging ? 'dragging' : ''} ${isMobile ? 'mobile-card' : ''}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      bodyStyle={isMobile ? { padding: '8px' } : { padding: '12px' }}
    >
      {isMobile ? (
        <div className="mobile-card-content">
          <div className="mobile-card-header">
            <div className="mobile-order-number">
              <Tag color="blue" style={{ fontWeight: 700, margin: 0 }}>#{order.number}</Tag>
            </div>
            <div className="mobile-header-right">
              {priorityMeta && (
                <Tag color={priorityMeta.color} style={{ margin: 0, fontSize: '11px' }}>
                  {priorityMeta.label}
                </Tag>
              )}
              <Tag color={statusInfo.color} style={{ margin: 0, fontSize: '11px' }}>
                {statusInfo.title}
              </Tag>
            </div>
          </div>
          
          <div className="mobile-card-body">
            <div className="mobile-card-row">
              <UserOutlined className="mobile-icon" />
              <span className="mobile-contragent">{contragentLabel}</span>
            </div>
            
            <div className="mobile-card-row">
              <EnvironmentOutlined className="mobile-icon" />
              <span className="mobile-address">{deliveryAddress || 'Не указано'}</span>
            </div>
            
            <div className="mobile-card-row compact">
              <IdcardOutlined className="mobile-icon" />
              <span className="mobile-assignee">{renderAssignee(order.assigned_picker)}</span>
              <UserOutlined className="mobile-icon" />
              <span className="mobile-assignee">{renderAssignee(order.assigned_courier)}</span>
            </div>
            
            <div className="mobile-card-row compact">
              <ClockCircleOutlined className="mobile-icon" />
              <span className="mobile-time">{formatDate(deliveryMoment)}</span>
              <span className="mobile-paid-status">{renderFullPaid(order)}</span>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="card-top">
            <div className="card-top-left">
              <div className="order-number">
                <Tag color="blue" style={{ fontWeight: 700, padding: '6px 10px', fontSize: 13 }}>#{order.number}</Tag>
              </div>
            </div>
            <div className="card-top-right">
              {priorityMeta && (
                <Tag color={priorityMeta.color} style={{ margin: 0 }}>
                  {priorityMeta.label}
                </Tag>
              )}
              <Tag color={statusInfo.color} style={{ margin: 0 }}>
                {statusInfo.title}
              </Tag>
            </div>
          </div>

          <div className="card-content">
            <div className="order-sum-compact">
              <div className="sum-value">{Number(order.sum || 0).toLocaleString('ru-RU')} ₽</div>
              <div className="sum-meta"><ShoppingCartOutlined /> {Number(order.nomenclature_count || 0)} шт.</div>
            </div>

            <div className="center-row">
              <div className="contragent">
                <UserOutlined className="ic" /> <span className="contragent-name">{contragentLabel}</span>
                {order.tags && Array.isArray(order.tags) && order.tags.length > 0 && (
                  <TagOutlined style={{ marginLeft: 8 }} />
                )}
              </div>
            </div>

            <div className="center-row">
              <div className="meta-inline">
                <div title="Сборщик" className="meta-item"><IdcardOutlined /> {renderAssignee(order.assigned_picker)}</div>
                <div title="Курьер" className="meta-item"><UserOutlined /> {renderAssignee(order.assigned_courier)}</div>
              </div>
            </div>

            <div className="center-row">
              <div className="address"><EnvironmentOutlined className="ic" /> {deliveryAddress || 'Не указано'}</div>
            </div>

            <div className="center-row small">
              <div className="time"><ClockCircleOutlined /> {formatDate(createdMoment)}</div>
              <div className="time"><CalendarOutlined /> {formatDate(deliveryMoment)}</div>
              <div className="paid-status">{renderFullPaid(order)}</div>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default DocsSalesCalendar;